import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/Layout";
import { EmptyState } from "@/components/EmptyState";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  CalendarClock,
  Loader2,
  Search,
  Pencil,
  Trash2,
  Play,
  Pause,
  Wrench,
  Clock3,
  TriangleAlert,
  CheckCircle2,
  RefreshCw,
  UserRound,
  Building2,
  Boxes,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  client_id: "",
  asset_id: "",
  title: "",
  description: "",
  frequency_value: 6,
  frequency_unit: "meses",
  next_due_date: "",
  technician_id: "",
  advance_notice_days: 7,
  auto_create_order: true,
  active: true,
  estimated_hours: 0,
  notes: "",
};

const UNIT_LABEL = {
  dias: "días",
  semanas: "semanas",
  meses: "meses",
  anos: "años",
};

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <div className="mantix-card flex items-center gap-4 p-5">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-2xl"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div>
        <p className="font-display text-2xl font-bold text-[#1D1D1F]">
          {value}
        </p>
        <p className="text-sm text-[#86868B]">{label}</p>
      </div>
    </div>
  );
}

export default function PreventiveMaintenance() {
  const [plans, setPlans] = useState([]);
  const [clients, setClients] = useState([]);
  const [assets, setAssets] = useState([]);
  const [technicians, setTechnicians] = useState([]);

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [processing, setProcessing] = useState(false);

  const load = async () => {
    setLoading(true);

    try {
      const [
        plansResponse,
        clientsResponse,
        techniciansResponse,
      ] = await Promise.all([
        api.get("/preventive-maintenance"),
        api.get("/clients"),
        api.get("/technicians"),
      ]);

      setPlans(plansResponse.data || []);
      setClients(clientsResponse.data || []);
      setTechnicians(techniciansResponse.data || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const boot = async () => {
      try {
        await api.post("/preventive-maintenance/process");
      } catch {
        // La pantalla debe abrir aunque el procesado automático falle.
      }

      await load();
    };

    boot();
  }, []);

  const loadAssetsForClient = async (clientId) => {
    if (!clientId) {
      setAssets([]);
      return;
    }

    try {
      const { data } = await api.get(
        `/assets?client_id=${encodeURIComponent(clientId)}`
      );
      setAssets(data || []);
    } catch (error) {
      setAssets([]);
      toast.error(
        formatApiError(error) ||
          "No se pudieron cargar los equipos del cliente."
      );
    }
  };

  const reset = () => {
    setEditing(null);
    setForm(EMPTY);
    setAssets([]);
  };

  const openCreate = () => {
    reset();
    setOpen(true);
  };

  const openEdit = async (plan) => {
    await loadAssetsForClient(plan.client_id);
    setEditing(plan);
    setForm({
      client_id: plan.client_id || "",
      asset_id: plan.asset_id || "",
      title: plan.title || "",
      description: plan.description || "",
      frequency_value: plan.frequency_value || 1,
      frequency_unit: plan.frequency_unit || "meses",
      next_due_date: plan.next_due_date
        ? String(plan.next_due_date).slice(0, 10)
        : "",
      technician_id: plan.technician_id || "",
      advance_notice_days: plan.advance_notice_days ?? 7,
      auto_create_order: plan.auto_create_order !== false,
      active: plan.active !== false,
      estimated_hours: plan.estimated_hours || 0,
      notes: plan.notes || "",
    });
    setOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();

    if (!form.client_id || !form.asset_id) {
      toast.error("Selecciona cliente y equipo.");
      return;
    }

    if (!form.title.trim()) {
      toast.error("Escribe el título del mantenimiento.");
      return;
    }

    if (!form.next_due_date) {
      toast.error("Selecciona la próxima fecha.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
        notes: form.notes.trim(),
        technician_id: form.technician_id || null,
        frequency_value:
          Number(form.frequency_value) || 1,
        advance_notice_days:
          Number(form.advance_notice_days) || 0,
        estimated_hours:
          Number(form.estimated_hours) || 0,
      };

      if (editing) {
        await api.put(
          `/preventive-maintenance/${editing.id}`,
          payload
        );
        toast.success("Mantenimiento actualizado.");
      } else {
        await api.post("/preventive-maintenance", payload);
        toast.success("Mantenimiento programado.");
      }

      setOpen(false);
      reset();
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (plan) => {
    try {
      await api.put(
        `/preventive-maintenance/${plan.id}`,
        { active: !plan.active }
      );

      toast.success(
        plan.active
          ? "Mantenimiento pausado."
          : "Mantenimiento activado."
      );
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const remove = async (plan) => {
    if (
      !window.confirm(
        `¿Eliminar el mantenimiento "${plan.title}"?`
      )
    ) {
      return;
    }

    setDeletingId(plan.id);

    try {
      await api.delete(
        `/preventive-maintenance/${plan.id}`
      );
      toast.success("Mantenimiento eliminado.");
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeletingId(null);
    }
  };

  const generateNow = async (plan) => {
    if (
      !window.confirm(
        `¿Crear ahora una orden para "${plan.title}"?`
      )
    ) {
      return;
    }

    setGeneratingId(plan.id);

    try {
      const { data } = await api.post(
        `/preventive-maintenance/${plan.id}/generate`
      );

      toast.success(
        data.created
          ? `Orden ${data.work_order?.number || ""} creada.`
          : "La orden de hoy ya existía."
      );
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setGeneratingId(null);
    }
  };

  const processNow = async () => {
    setProcessing(true);

    try {
      const { data } = await api.post(
        "/preventive-maintenance/process"
      );

      toast.success(
        data.created_orders
          ? `${data.created_orders} orden(es) automática(s) creada(s).`
          : "No había mantenimientos vencidos por generar."
      );

      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setProcessing(false);
    }
  };

  const filteredAssets = assets.filter(
    (asset) => asset.status !== "baja"
  );

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();

    return plans.filter((plan) => {
      const matchesSearch =
        !normalized ||
        [
          plan.title,
          plan.client_name,
          plan.asset_name,
          plan.asset_serial_number,
          plan.technician_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalized);

      let matchesStatus = true;

      if (statusFilter === "activos") {
        matchesStatus = plan.active !== false;
      } else if (statusFilter === "pausados") {
        matchesStatus = plan.active === false;
      } else if (statusFilter === "vencidos") {
        matchesStatus = Boolean(plan.overdue);
      } else if (statusFilter === "proximos") {
        matchesStatus = Boolean(plan.due_soon);
      }

      return matchesSearch && matchesStatus;
    });
  }, [plans, query, statusFilter]);

  const activeCount = plans.filter(
    (plan) => plan.active !== false
  ).length;
  const overdueCount = plans.filter(
    (plan) => plan.overdue
  ).length;
  const dueSoonCount = plans.filter(
    (plan) => plan.due_soon
  ).length;

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <PageHeader
        title="Mantenimiento preventivo"
        subtitle="Programa revisiones y crea órdenes automáticamente"
        action={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={processNow}
              disabled={processing}
              className="flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-medium text-[#0066CC] shadow-sm disabled:opacity-60"
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Procesar ahora
            </button>

            <Dialog
              open={open}
              onOpenChange={(value) => {
                setOpen(value);
                if (!value) reset();
              }}
            >
              <DialogTrigger asChild>
                <button
                  type="button"
                  onClick={openCreate}
                  className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.3)]"
                >
                  <Plus className="h-4 w-4" />
                  Nuevo mantenimiento
                </button>
              </DialogTrigger>

              <DialogContent className="max-h-[94vh] max-w-3xl overflow-y-auto rounded-3xl border-none bg-white p-6">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">
                    {editing
                      ? "Editar mantenimiento"
                      : "Programar mantenimiento"}
                  </DialogTitle>
                </DialogHeader>

                <form onSubmit={save} className="mt-5 space-y-5">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium">
                        Cliente
                      </label>
                      <select
                        value={form.client_id}
                        onChange={async (event) => {
                          const clientId = event.target.value;

                          setForm({
                            ...form,
                            client_id: clientId,
                            asset_id: "",
                          });

                          await loadAssetsForClient(clientId);
                        }}
                        required
                        className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                      >
                        <option value="">Selecciona cliente</option>
                        {clients.map((client) => (
                          <option
                            key={client.id}
                            value={client.id}
                          >
                            {client.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium">
                        Equipo
                      </label>
                      <select
                        value={form.asset_id}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            asset_id: event.target.value,
                          })
                        }
                        disabled={!form.client_id}
                        required
                        className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none disabled:opacity-50"
                      >
                        <option value="">
                          {!form.client_id
                            ? "Selecciona primero un cliente"
                            : filteredAssets.length === 0
                              ? "Este cliente no tiene equipos"
                              : "Selecciona equipo"}
                        </option>
                        {filteredAssets.map((asset) => (
                          <option
                            key={asset.id}
                            value={asset.id}
                          >
                            {asset.name}
                            {asset.serial_number
                              ? ` · ${asset.serial_number}`
                              : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Título
                    </label>
                    <input
                      value={form.title}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          title: event.target.value,
                        })
                      }
                      placeholder="Revisión semestral de climatización"
                      required
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Trabajo a realizar
                    </label>
                    <textarea
                      rows={3}
                      value={form.description}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          description: event.target.value,
                        })
                      }
                      className="w-full resize-none rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium">
                        Cada
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={form.frequency_value}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            frequency_value: event.target.value,
                          })
                        }
                        required
                        className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium">
                        Periodo
                      </label>
                      <select
                        value={form.frequency_unit}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            frequency_unit: event.target.value,
                          })
                        }
                        className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                      >
                        <option value="dias">Días</option>
                        <option value="semanas">Semanas</option>
                        <option value="meses">Meses</option>
                        <option value="anos">Años</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium">
                        Próxima revisión
                      </label>
                      <input
                        type="date"
                        value={form.next_due_date}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            next_due_date: event.target.value,
                          })
                        }
                        required
                        className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium">
                        Técnico
                      </label>
                      <select
                        value={form.technician_id}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            technician_id: event.target.value,
                          })
                        }
                        className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                      >
                        <option value="">Sin asignar</option>
                        {technicians.map((technician) => (
                          <option
                            key={technician.id}
                            value={technician.id}
                          >
                            {technician.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium">
                        Avisar antes
                      </label>
                      <div className="flex items-center rounded-xl bg-[#F5F5F7]">
                        <input
                          type="number"
                          min="0"
                          value={form.advance_notice_days}
                          onChange={(event) =>
                            setForm({
                              ...form,
                              advance_notice_days:
                                event.target.value,
                            })
                          }
                          className="min-w-0 flex-1 bg-transparent px-4 py-3 outline-none"
                        />
                        <span className="pr-4 text-sm text-[#86868B]">
                          días
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium">
                        Horas estimadas
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.25"
                        value={form.estimated_hours}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            estimated_hours: event.target.value,
                          })
                        }
                        className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="flex cursor-pointer items-center justify-between rounded-2xl bg-[#F5F5F7] p-4">
                      <span>
                        <span className="block text-sm font-medium">
                          Crear OT automáticamente
                        </span>
                        <span className="mt-0.5 block text-xs text-[#86868B]">
                          Cuando llegue la fecha.
                        </span>
                      </span>
                      <input
                        type="checkbox"
                        checked={form.auto_create_order}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            auto_create_order:
                              event.target.checked,
                          })
                        }
                        className="h-5 w-5 accent-[#0066CC]"
                      />
                    </label>

                    <label className="flex cursor-pointer items-center justify-between rounded-2xl bg-[#F5F5F7] p-4">
                      <span>
                        <span className="block text-sm font-medium">
                          Plan activo
                        </span>
                        <span className="mt-0.5 block text-xs text-[#86868B]">
                          Puedes pausarlo cuando quieras.
                        </span>
                      </span>
                      <input
                        type="checkbox"
                        checked={form.active}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            active: event.target.checked,
                          })
                        }
                        className="h-5 w-5 accent-[#0066CC]"
                      />
                    </label>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Notas internas
                    </label>
                    <textarea
                      rows={2}
                      value={form.notes}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          notes: event.target.value,
                        })
                      }
                      className="w-full resize-none rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="flex-1 rounded-full border border-[#D2D2D7] py-3 font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white disabled:opacity-60"
                    >
                      {saving && (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      )}
                      {saving
                        ? "Guardando..."
                        : editing
                          ? "Guardar cambios"
                          : "Programar"}
                    </button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard
          icon={CalendarClock}
          label="Planes activos"
          value={activeCount}
          color="#0066CC"
        />
        <SummaryCard
          icon={TriangleAlert}
          label="Vencidos"
          value={overdueCount}
          color="#FF3B30"
        />
        <SummaryCard
          icon={Clock3}
          label="Próximos"
          value={dueSoonCount}
          color="#FF9500"
        />
      </section>

      <section className="mantix-card mb-6 p-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />
            <input
              value={query}
              onChange={(event) =>
                setQuery(event.target.value)
              }
              placeholder="Buscar cliente, equipo, técnico..."
              className="w-full rounded-xl bg-[#F5F5F7] py-3 pl-11 pr-4 text-sm outline-none"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(event.target.value)
            }
            className="rounded-xl bg-[#F5F5F7] px-4 py-3 text-sm outline-none"
          >
            <option value="todos">Todos</option>
            <option value="activos">Activos</option>
            <option value="proximos">Próximos</option>
            <option value="vencidos">Vencidos</option>
            <option value="pausados">Pausados</option>
          </select>
        </div>
      </section>

      {loading ? (
        <div className="flex min-h-72 items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-[#0066CC]" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No hay mantenimientos programados"
          subtitle="Programa una revisión periódica para uno de los equipos de tus clientes."
        />
      ) : (
        <div className="space-y-4">
          {filtered.map((plan) => (
            <article
              key={plan.id}
              className={`mantix-card p-5 ${
                plan.overdue
                  ? "ring-1 ring-[#FF3B30]/30"
                  : ""
              }`}
            >
              <div className="flex flex-col justify-between gap-4 lg:flex-row">
                <div className="flex min-w-0 items-start gap-3">
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${
                      plan.overdue
                        ? "bg-[#FFEBEA] text-[#D70015]"
                        : plan.due_soon
                          ? "bg-[#FFF4E5] text-[#C93400]"
                          : "bg-[#EAF2FC] text-[#0066CC]"
                    }`}
                  >
                    <Wrench className="h-5 w-5" />
                  </div>

                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-[#1D1D1F]">
                        {plan.title}
                      </h3>

                      {!plan.active && (
                        <span className="rounded-full bg-[#F5F5F7] px-2.5 py-1 text-xs text-[#86868B]">
                          Pausado
                        </span>
                      )}

                      {plan.overdue && (
                        <span className="rounded-full bg-[#FFEBEA] px-2.5 py-1 text-xs font-medium text-[#D70015]">
                          Vencido
                        </span>
                      )}

                      {plan.due_soon && !plan.overdue && (
                        <span className="rounded-full bg-[#FFF4E5] px-2.5 py-1 text-xs font-medium text-[#C93400]">
                          Próximo
                        </span>
                      )}
                    </div>

                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#86868B]">
                      <span className="flex items-center gap-1.5">
                        <Building2 className="h-4 w-4" />
                        {plan.client_name}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Boxes className="h-4 w-4" />
                        {plan.asset_name}
                      </span>
                      {plan.technician_name && (
                        <span className="flex items-center gap-1.5">
                          <UserRound className="h-4 w-4" />
                          {plan.technician_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="shrink-0 text-left lg:text-right">
                  <p className="text-xs text-[#86868B]">
                    Próxima revisión
                  </p>
                  <p
                    className={`mt-1 font-display text-xl font-bold ${
                      plan.overdue
                        ? "text-[#D70015]"
                        : "text-[#1D1D1F]"
                    }`}
                  >
                    {plan.next_due_date}
                  </p>
                  <p className="mt-1 text-xs text-[#86868B]">
                    Cada {plan.frequency_value}{" "}
                    {UNIT_LABEL[plan.frequency_unit] ||
                      plan.frequency_unit}
                  </p>
                </div>
              </div>

              {plan.description && (
                <p className="mt-4 rounded-xl bg-[#F5F5F7] p-3 text-sm text-[#6E6E73]">
                  {plan.description}
                </p>
              )}

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[#E8E8ED] pt-4">
                <div className="text-xs text-[#86868B]">
                  {plan.auto_create_order
                    ? "✓ OT automática activada"
                    : "OT automática desactivada"}
                  {plan.last_generated_date
                    ? ` · Última generada: ${plan.last_generated_date}`
                    : ""}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => generateNow(plan)}
                    disabled={generatingId === plan.id}
                    className="flex items-center gap-1.5 rounded-full bg-[#E9F9EE] px-4 py-2 text-sm font-medium text-[#248A3D] disabled:opacity-60"
                  >
                    {generatingId === plan.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                    Crear OT ahora
                  </button>

                  <button
                    type="button"
                    onClick={() => toggleActive(plan)}
                    className="flex items-center gap-1.5 rounded-full bg-[#F5F5F7] px-4 py-2 text-sm font-medium"
                  >
                    {plan.active ? (
                      <Pause className="h-4 w-4" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                    {plan.active ? "Pausar" : "Activar"}
                  </button>

                  <button
                    type="button"
                    onClick={() => openEdit(plan)}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F5F5F7] text-[#0066CC]"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    onClick={() => remove(plan)}
                    disabled={deletingId === plan.id}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-[#B0B0B5] hover:bg-[#FFEBEA] hover:text-[#FF3B30]"
                  >
                    {deletingId === plan.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
